import React, {useEffect, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useParams} from "react-router";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import materialGroupService from "../../../../../services/web/material-group-service.js";
import {FaPlus} from "react-icons/fa";
import useFetchOptions from "../../hooks/use-fetch-options.js";
import materialXStorageService from "../../../../../services/web/material-x-storage-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";
import {format} from "date-fns";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import materialMaintenanceService from "../../../../../services/web/material-maintenance-service.js";
import Attach from "../../../../../components/attach/attach.jsx";
import materialService from "../../../../../services/web/material-service.js";
import materialUniqueService from "../../../../../services/web/material-unique-service.js";
import {unavailableReasonOptions} from "../utils/maintenances-utils.jsx";

const initialData = {
    id: "",
    storage_id: "",
    group: "",
    material_x_storage_id: "",
    unavailable_reason: "",
    return_estimate: "",
    note_send: "",
    note_return: "",
    report: "",
    breakdown: "",
}

const MaterialMaintenancesForm = () => {
    const navigate = navigateHook();
    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState(initialData)
    const [materialDescription, setMaterialDescription] = useState("")
    const [hasReturn, setHasReturn] = useState(false)
    const [initializedId, setInitializedId] = useState(null)
    const {
        storageByUserOptions,
        isLoadingStorageByUserOptions,
        isErrorStorageByUserOptions,
        refetchStorageByUserOptions,
    } = useFetchOptions({fetchStorageByUser: true})

    const onChangeSelect = (id, value) => setData(prev => ({...prev, [id]: value || ""}));
    const onChangeStorage = (value) => {
        setData(prev => ({
            ...prev,
            storage_id: value || "",
            group: "",
            material_x_storage_id: "",
            breakdown: "",
        }))
    }
    const onChangeGroup = (value) => {
        setData(prev => ({
            ...prev,
            group: value || "",
            material_x_storage_id: "",
            breakdown: "",
        }))
    }
    const handleDate = (id, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null
        setData(prev => ({...prev, [id]: dateFormated}))
    }

    const onChangeFile = (file, id) => {
        if (file) {
            setData(prev => ({...prev, [id]: file}))
        }
    }

    const removeFile = (id) => {
        setData(prev => ({...prev, [id]: ""}))
    }

    const {
        data: initializationData,
        isFetching: isFetchingInitialization,
        isError: isInitializationError,
        refetch,
    } = useQuery({
        queryKey: ['material-maintenance', id],
        queryFn: async () => {
            const maintenance = await materialMaintenanceService.getById(id)
            const {
                storage_id,
                return_estimate,
                note_send,
                note_return,
                unavailable_reason,
                report,
                material_id,
                unique_id,
                movement_return_id,
            } = maintenance

            const [material, materialUnique, groups] = await Promise.all([
                materialService.getById(material_id),
                materialUniqueService.getById(unique_id),
                materialGroupService.options(storage_id),
            ])

            const normalizedData = projectUtils.normalizeData({
                storage_id,
                return_estimate: return_estimate ? return_estimate.split(" ")[0] : "",
                note_send,
                note_return,
                unavailable_reason,
                report,
                group: material.group_id,
                breakdown: materialUnique.breakdown,
            })

            return {
                data: {...initialData, ...normalizedData},
                groups,
                materialDescription: materialUnique.description,
                hasReturn: !!movement_return_id,
            }
        },
        enabled: !!id
    });

    useEffect(() => {
        if (!id) {
            setData(initialData)
            setMaterialDescription("")
            setHasReturn(false)
            setInitializedId(null)
            return
        }

        if (!initializationData) return

        setData(initializationData.data)
        setMaterialDescription(initializationData.materialDescription)
        setHasReturn(initializationData.hasReturn)
        setInitializedId(id)
    }, [id, initializationData]);

    const {
        data: creationGroups,
        isLoading: isLoadingGroups,
        isError: isErrorGroups,
        refetch: refetchGroups,
    } = useQuery({
        queryKey: ['material-groups', data.storage_id],
        queryFn: () => materialGroupService.options(data.storage_id),
        enabled: !id && !!data.storage_id
    });

    const groups = id ? initializationData?.groups : creationGroups

    useEffect(() => {
        if (id || !groups) return

        const groupSelected = groups && groups.length === 1 ? groups[0].value : ""
        setData(prev => ({...prev, group: groupSelected}))
    }, [groups, id]);

    const {
        data: materials,
        isLoading: isLoadingMaterials,
        isError: isErrorMaterials,
        refetch: refetchMaterials,
    } = useQuery({
        queryKey: ['materials', data.group, data.storage_id],
        queryFn: () => materialXStorageService.optionsUniqueByGroupAndStorage(data.group, data.storage_id, false),
        enabled: !id && !!data.group && !!data.storage_id
    });

    const {
        data: materialSelected,
        isLoading: isLoadingMaterial,
        isError: isErrorMaterial,
        refetch: refetchMaterial,
    } = useQuery({
        queryKey: ['material-x-storage', data.material_x_storage_id],
        queryFn: () => materialXStorageService.getById(data.material_x_storage_id),
        enabled: !id && !!data.material_x_storage_id
    });

    useEffect(() => {
        if (id) return

        const breakdown = materialSelected?.unique?.breakdown ?? ""
        setData(prevState => ({...prevState, breakdown }))
    }, [id, materialSelected]);

    const save = () => {
        setIsLoading(true);

        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value ?? "");
        });

        materialMaintenanceService.save(formData, id)
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch()
                    return;
                }

                const urlDev = `/materials/maintenances/form/${data.id}`;
                navigate(urlDev);

            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    const openForm = () => {
        const url = `/materials/maintenances/form`;
        setData(initialData)
        setMaterialDescription("")
        setHasReturn(false)
        navigate(url)
    }

    const hasLoadingError = isErrorStorageByUserOptions
        || (!!id && isInitializationError)
        || (!id && (isErrorGroups || isErrorMaterials || isErrorMaterial))

    const isFormLoading = isLoading
        || isLoadingStorageByUserOptions
        || (!!id && !isInitializationError && (isFetchingInitialization || initializedId !== id))
        || (!id && initializedId !== null)
        || (!id && (isLoadingGroups || isLoadingMaterials || isLoadingMaterial))

    const retryLoading = () => {
        if (isErrorStorageByUserOptions) refetchStorageByUserOptions()
        if (id && isInitializationError) refetch()
        if (!id && isErrorGroups) refetchGroups()
        if (!id && isErrorMaterials) refetchMaterials()
        if (!id && isErrorMaterial) refetchMaterial()
    }

    return (<Template isLoading={isFormLoading}>
        <Template.Header>
            <Template.Title>
                Indisponibilidade de material
            </Template.Title>
            {
                id &&
                <Button target={"primary-outline"} onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar indisponibilidade</span>
                </Button>
            }
        </Template.Header>
        {hasLoadingError ? (
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="flex flex-col items-center gap-4 py-8">
                            <span>Não foi possível carregar os dados do formulário.</span>
                            <Button onClick={retryLoading}>Tentar novamente</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        ) : (
        <Template.Body>
            <Card>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                            <SelectComponent
                                isDisabled={!!id}
                                id="storage_id"
                                name="storage_id"
                                value={data.storage_id}
                                options={storageByUserOptions || []}
                                label={"Depósito *"}
                                onChange={(selected) => onChangeStorage(selected?.value)}
                            />
                        </div>

                        {id ? (

                        <div className="col-span-3">
                            <SelectComponent
                                isDisabled={!!id}
                                id="group"
                                name="group"
                                value={data.group}
                                options={groups || []}
                                label={"Grupo *"}
                                onChange={(selected) => onChangeGroup(selected?.value)}
                            />
                        </div>
                        ) : (
                            <div className="col-span-3">
                                <SelectComponent
                                    isDisabled={!!id}
                                    id="group"
                                    name="group"
                                    value={data.group}
                                    options={groups || []}
                                    label={"Grupo *"}
                                    onChange={(selected) => onChangeGroup(selected?.value)}
                                />
                            </div>
                        )}
                        {
                            id &&
                            <div className={"col-span-6"}>
                                <InputForm disabled={true} value={materialDescription} label="Material *" onChange={() => {}}/>
                            </div>
                        }
                        {
                            !id &&
                            <div className={"col-span-6"}>
                                <SelectComponent
                                    id="material_x_storage_id"
                                    name="material_x_storage_id"
                                    value={data.material_x_storage_id}
                                    options={materials || []}
                                    label={"Material *"}
                                    onChange={(selected) => onChangeSelect("material_x_storage_id", selected?.value)}
                                />
                            </div>
                        }
                        <div className="col-span-12">
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-3">
                                    <SelectComponent
                                        isDisabled={!!id}
                                        id="unavailable_reason"
                                        name="unavailable_reason"
                                        label="Motivo *"
                                        value={data.unavailable_reason}
                                        options={unavailableReasonOptions}
                                        onChange={(selected) => onChangeSelect("unavailable_reason", selected?.value)}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <InputDateTime
                                        disabled={!!id}
                                        id={"return_estimate"}
                                        selected={data.return_estimate}
                                        label={"Estimativa de retorno"}
                                        dateFormat={"L"}
                                        onChange={(date) => handleDate("return_estimate", date)}
                                    />
                                </div>
                                {
                                    ((id && !hasReturn) || !!data.report) &&
                                    <div className="col-span-2">
                                        <Attach
                                            onChangeFile={(file) => onChangeFile(file, "report")}
                                            removeFile={() => removeFile("report")}
                                            itemId={"report"}
                                            showRemove={!hasReturn}
                                            attachId={data.report}/>
                                    </div>
                                }
                            </div>

                        </div>

                        <div className={"col-span-12"}>
                            <InputTextarea
                                disabled={hasReturn}
                                id={"breakdown"}
                                className={"col-span-full"}
                                textAreaClass={"h-40"}
                                label={"Avaria"}
                                value={data.breakdown}
                                onChange={({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))}
                            />
                        </div>

                        <div className={id ? "col-span-6" : "col-span-12"}>
                            <InputTextarea
                                disabled={!!id}
                                id={"note_send"}
                                className={"col-span-full"}
                                textAreaClass={"h-40"}
                                label={"Descrição *"}
                                value={data.note_send}
                                onChange={({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))}
                            />
                        </div>

                        {
                            id &&
                            <>
                                <div className={"col-span-6"}>
                                    <InputTextarea
                                        disabled={hasReturn}
                                        id={"note_return"}
                                        className={"col-span-full"}
                                        textAreaClass={"h-40"}
                                        label={"Observação de retorno *"}
                                        value={data.note_return}
                                        onChange={({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))}
                                    />
                                </div>
                            </>
                        }
                    </div>
                </Card.Body>
            </Card>

            {
                !hasReturn &&
                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="flex justify-end gap-2 w-full">
                            <Button onClick={save} actionFeedbackType='save'>Salvar</Button>
                        </div>
                    </Card.Body>
                </Card>
            }
        </Template.Body>
        )}
    </Template>);
};

export default MaterialMaintenancesForm;